/**
 * ContentOutlinePreviewSlot
 *
 * Renders a live-polling task queue table for the blog post content strategizer
 * inside a chat behaviour slot. Fetches data from key-val store buckets (4-bucket pattern)
 * and displays a read-only table with status updates.
 *
 * Action buttons navigate to the full agent page rather than opening
 * modals inline, keeping the slot as a lightweight preview.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { IconButton } from '@strivelabs/ui-core/v2';
import { LoadingSkeleton } from '@strivelabs/ui-blocks';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';
import { z } from 'zod/v4';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { useContentOutlineData } from '@/hooks/useContentOutlineData';
import { ROUTES } from '@/agents/blog-post-content-strategizer/constants';
import { ContentOutlineTable } from '@/components/content-outline/ContentOutlineTable';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import { useStoreAndFetch } from '@/hooks/useStoreAndFetch';
import { getBucketConfig } from '@/constants/contentRefreshBucketRegistry';
import { useResolveWorkflowInstanceId } from '@/hooks/useResolveWorkflowInstanceId';
import { usePersistentEntryStore } from '@/hooks/usePersistentEntryStore';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  parseKeywords,
  getSlotActionButton,
} from './shared';

/** Zod schema for Content Outline entry validation */
const ContentOutlineEntrySchema = z.object({
  primary_keywords: z.union([z.array(z.string()), z.string()]).optional(),
  secondary_keywords: z.union([z.array(z.string()), z.string()]).optional(),
  product_name: z.string().optional(),
  content_focus_angle: z.string().optional(),
  content_goal: z.string().optional(),
  outline_type: z.string().optional(),
  plp_url: z.string().optional(),
});

export interface ContentOutlinePreviewSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  action: BehaviourAgentExperienceAction;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function ContentOutlinePreviewSlot({
  slotId,
  action,
  chatHistoryId,
  behaviourActionState,
}: ContentOutlinePreviewSlotProps) {
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
    chatHistoryId,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const agentShortName = action.agent_short_name;
  const resolvedWorkflowInstanceId = useResolveWorkflowInstanceId(agentShortName);
  const workflowInstanceId = resolvedWorkflowInstanceId ?? '';
  const entries = action.data?.entries;

  const { workflowInstanceId: ctxWorkflowInstanceId, workflowExecutionId } = useChatMetadata();

  // Use hook to manage persistent entry storage state
  const { alreadyStored, handleStoreSuccess } = usePersistentEntryStore(
    action,
    behaviourActionState,
    chatHistoryId,
    ctxWorkflowInstanceId,
    workflowExecutionId
  );

  // Get bucket configuration for this agent
  const bucketConfig = getBucketConfig(agentShortName);

  // Transform entry from backend format to store format
  const transformEntry = useCallback((entry: unknown) => {
    const parsed = ContentOutlineEntrySchema.safeParse(entry);
    if (!parsed.success) {
      // Validation failed - return default values
      console.warn('[ContentOutlinePreviewSlot] Invalid entry format:', parsed.error);
      return {
        primary_keywords: [],
        secondary_keywords: [],
      };
    }
    const e = parsed.data;

    const result: Record<string, unknown> = {
      primary_keywords: parseKeywords(e.primary_keywords),
      secondary_keywords: parseKeywords(e.secondary_keywords),
    };

    // Add optional fields if present
    if (e.product_name) result.product_name = e.product_name;
    if (e.content_focus_angle) result.content_focus_angle = e.content_focus_angle;
    if (e.content_goal) result.content_goal = e.content_goal;
    if (e.outline_type) result.outline_type = e.outline_type;
    if (e.plp_url) result.plp_url = e.plp_url;

    return result;
  }, []);

  // Store entries first (if provided), then enable fetching
  // If entries were already stored (from behaviour_action_state), skip store phase
  const { shouldFetch, isStoring, storeError } = useStoreAndFetch({
    entries,
    workflowInstanceId,
    bucketName: bucketConfig?.todoBucket ?? '',
    appName: bucketConfig?.appName ?? '',
    agentShortName,
    transformEntry,
    alreadyStored,
    onStoreSuccess: handleStoreSuccess,
    // Strategizer no longer relies on a scheduled trigger to drain TODO,
    // so each stored entry needs its own manual trigger with kv_entry_key.
    triggerAfterStore: true,
  });

  const { data, isLoading, isError } = useContentOutlineData({
    workflowInstanceId,
    agentShortName,
    enabled: !!workflowInstanceId,
    shouldFetch,
  });

  const handleNavigateToAgent = useCallback(() => {
    navigate(ROUTES.BASE);
  }, [navigate]);

  const renderAction = useCallback(
    (row: { _status: ProcessingStatus; data?: { workflow_execution_id?: string }; key?: string }) => {
      // For completed rows, navigate directly to the result page
      if (row._status === ProcessingStatus.COMPLETED && row.data?.workflow_execution_id && row.key) {
        const resultUrl = `${ROUTES.result(row.data.workflow_execution_id)}?key=${row.key}`;
        return getSlotActionButton({
          status: row._status,
          onNavigateToAgent: () => navigate(resultUrl),
        });
      }
      return getSlotActionButton({
        status: row._status,
        onNavigateToAgent: handleNavigateToAgent,
      });
    },
    [handleNavigateToAgent, navigate]
  );

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  if (!workflowInstanceId) return null;

  // -- Render helpers ---------------------------------------------------------

  const renderBodyContent = () => {
    if (isStoring) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Adding entries to task queue...
          </p>
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      );
    }
    if (isError || data.length === 0) {
      return (
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError
              ? 'Unable to load content outline tasks.'
              : 'No content outline tasks found.'}
          </p>
        </div>
      );
    }
    return (
      <>
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Failed to add new entries. Showing existing entries only.
            </p>
          </div>
        )}
        <div className={storeError ? 'px-6 pb-6' : 'p-8'}>
          <ContentOutlineTable data={data} renderAction={renderAction} />
        </div>
      </>
    );
  };

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    return (
      <>
        <SlotCanvasPreview
          id={resolvedSlotId}
          experience={action.experience}
          title={resolvedTitle}
          isActive={canvasRegistration.isOpen}
          onClick={canvasRegistration.open}
        />
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
            >
              {renderBodyContent()}
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------

  // Store phase loading state
  if (isStoring) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Adding entries to task queue...
          </p>
        </div>
      </SlotContainer>
    );
  }

  // Fetch phase loading state
  if (isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (isError || data.length === 0) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError ? 'Unable to load content outline tasks.' : 'No content outline tasks found.'}
          </p>
        </div>
      </SlotContainer>
    );
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <IconButton variant="secondary" size="2xs"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </IconButton>

        {/* Store error warning */}
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Failed to add new entries. Showing existing entries only.
            </p>
          </div>
        )}

        {/* Table content */}
        <div className={storeError ? "px-6 pb-6" : "p-8"}>
          <ContentOutlineTable data={data} renderAction={renderAction} />
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            <ContentOutlineTable data={data} renderAction={renderAction} />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
}
